/**
 * BrowserNotificationManager Component
 * Manages browser notification permissions and displays native notifications
 */

import { useEffect, useRef } from 'react';
import { useNotificationStore } from '@/stores/notification';
import { useNavigate } from 'react-router-dom';

const BrowserNotificationManager: React.FC = () => {
  const navigate = useNavigate();
  const {
    notifications,
    isPermissionGranted,
    preferences,
    setPermissionGranted,
  } = useNotificationStore();
  
  const processedNotifications = useRef<Set<string>>(new Set());
  const permissionRequested = useRef(false);

  // Request browser notification permission
  useEffect(() => {
    if (permissionRequested.current) return;
    if (!preferences.browserNotifications) return;
    if (isPermissionGranted) return;

    const requestPermission = async () => {
      if ('Notification' in window && Notification.permission === 'default') {
        permissionRequested.current = true;
        const permission = await Notification.requestPermission();
        setPermissionGranted(permission === 'granted');
      } else if (Notification.permission === 'granted') {
        setPermissionGranted(true);
      }
    };

    // Request permission on first intervention
    if (notifications.length > 0 && !isPermissionGranted) {
      requestPermission();
    }
  }, [notifications.length, isPermissionGranted, preferences.browserNotifications, setPermissionGranted]);

  // Check if in quiet hours
  const isInQuietHours = (): boolean => {
    if (!preferences.quietHoursEnabled) return false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMin] = preferences.quietHoursStart.split(':').map(Number);
    const [endHour, endMin] = preferences.quietHoursEnd.split(':').map(Number);
    
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    if (startTime < endTime) {
      return currentTime >= startTime && currentTime < endTime;
    } else {
      // Quiet hours span midnight
      return currentTime >= startTime || currentTime < endTime;
    }
  };

  // Display browser notifications for new unread notifications
  useEffect(() => {
    if (!isPermissionGranted) return;
    if (!preferences.browserNotifications) return;
    if (document.visibilityState === 'visible') return; // Don't show when tab is active
    if (isInQuietHours()) return;

    const unreadNotifications = notifications.filter(
      (n) => !n.read && !processedNotifications.current.has(n.id)
    );

    unreadNotifications.forEach((notification) => {
      // Check if this notification type is enabled
      if (!preferences.enabledTypes[notification.type]) return;

      processedNotifications.current.add(notification.id);

      const notificationInstance = new Notification(notification.title, {
        body: notification.message,
        icon: '/notification-icon.png', // Add icon later
        tag: `intervention_${notification.interventionId}`,
        requireInteraction: notification.priority === 'CRITICAL',
        badge: '/badge-icon.png',
        data: {
          interventionId: notification.interventionId,
          projectId: notification.projectId,
          notificationId: notification.id,
        },
      });

      notificationInstance.onclick = () => {
        window.focus();
        navigate(`/interventions`);
        notificationInstance.close();
      };
    });
  }, [
    notifications,
    isPermissionGranted,
    preferences.browserNotifications,
    preferences.enabledTypes,
    navigate,
  ]);

  // Cleanup processed notifications that are removed
  useEffect(() => {
    const currentIds = new Set(notifications.map((n) => n.id));
    const toRemove: string[] = [];

    processedNotifications.current.forEach((id) => {
      if (!currentIds.has(id)) {
        toRemove.push(id);
      }
    });

    toRemove.forEach((id) => processedNotifications.current.delete(id));
  }, [notifications]);

  return null; // This component doesn't render anything
};

export default BrowserNotificationManager;
